"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "convex/react";
import { Eye } from "@/components/icons";
import { api } from "@convex/_generated/api";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/lib/formatting/dates";
import { handleError } from "@/lib/handle-error";
import { useReservedBannerSpace } from "./use-reserved-banner-space";
import { useActiveViewAs } from "./use-active-view-as";

function useViewAsExit(expiresAt: number | undefined) {
  const stop = useMutation(api.viewAs.mutations.stop);
  const router = useRouter();
  const [isExiting, setIsExiting] = useState(false);

  const exit = useCallback(async () => {
    setIsExiting(true);
    try {
      const ended = await stop({});
      router.push(ended ? `/admin/users/${ended.targetUserId}` : "/admin");
    } catch (error) {
      handleError(error, { fallbackMessage: "Could not exit view-as" });
    } finally {
      setIsExiting(false);
    }
  }, [stop, router]);

  useEffect(() => {
    if (expiresAt === undefined) return;
    const timer = setTimeout(
      () => void exit(),
      Math.max(0, expiresAt - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [expiresAt, exit]);

  return { exit, isExiting };
}

export function ViewAsBanner() {
  const viewing = useActiveViewAs();
  const { exit, isExiting } = useViewAsExit(viewing?.expiresAt);
  const bannerRef = useReservedBannerSpace<HTMLDivElement>(Boolean(viewing));

  if (!viewing) return null;

  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 bg-foreground px-4 py-2 text-sm text-background"
    >
      <Eye className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        <span className="font-semibold">Viewing as {viewing.targetName}</span>{" "}
        ({viewing.targetEmail}). Read-only: every change is refused. Ends{" "}
        {formatDateTime(viewing.expiresAt)}.
      </p>
      <Button
        size="sm"
        variant="primary"
        onClick={() => void exit()}
        disabled={isExiting}
      >
        Exit view-as
      </Button>
    </div>
  );
}
